import { AuthenticationDetails, CognitoRefreshToken, CognitoUser, CognitoUserPool, type CognitoUserSession } from "amazon-cognito-identity-js";
import type { Role } from "./data.js";

const userPoolId = import.meta.env.VITE_USER_POOL_ID;
const userPoolClientId = import.meta.env.VITE_USER_POOL_CLIENT_ID;
const apiEndpoint = import.meta.env.VITE_API_ENDPOINT;
export const authConfigured = Boolean(userPoolId && userPoolClientId && apiEndpoint);

const pool = authConfigured ? new CognitoUserPool({
  UserPoolId: userPoolId,
  ClientId: userPoolClientId,
  Storage: window.localStorage
}) : null;

export interface AdminSession {
  readonly email: string;
  readonly role: Role;
  readonly idToken: string;
}

interface StoredSession {
  readonly email: string;
  readonly idToken: string;
  readonly refreshToken: string;
}

const emailKey = "reevir.admin.email";
const tokenKey = "reevir.admin.idToken";
const refreshKey = "reevir.admin.refreshToken";

function user(email: string) {
  if (pool === null) throw new Error("Admin authentication is not configured.");
  return new CognitoUser({ Username: email, Pool: pool, Storage: window.localStorage });
}

export async function signIn(email: string, password: string): Promise<AdminSession> {
  const cognitoUser = user(email);
  const session = await new Promise<CognitoUserSession>((resolve, reject) => {
    cognitoUser.authenticateUser(new AuthenticationDetails({ Username: email, Password: password }), {
      onSuccess: resolve,
      onFailure: error => reject(new Error(authErrorMessage(error, "Sign-in failed."))),
      mfaRequired: () => reject(new Error("MFA challenge is not enabled in Admin Portal yet.")),
      totpRequired: () => reject(new Error("MFA challenge is not enabled in Admin Portal yet.")),
      newPasswordRequired: () => reject(new Error("Use forgot password to create your Admin Portal password."))
    });
  });
  const stored = readSession(email, session);
  saveStoredSession(stored);
  return sessionWithRole(stored);
}

export async function requestPasswordReset(email: string): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    user(email).forgotPassword({
      onSuccess: () => resolve(),
      inputVerificationCode: () => resolve(),
      onFailure: (error) => {
        if (error.name === "UserNotFoundException") resolve();
        else reject(new Error(authErrorMessage(error, "Password reset could not be started.")));
      }
    });
  });
}

export async function confirmPasswordReset(email: string, code: string, password: string): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    user(email).confirmPassword(code, password, { onSuccess: () => resolve(), onFailure: error => reject(new Error(authErrorMessage(error, "Password reset failed."))) });
  });
}

function authErrorMessage(error: { readonly name?: string; readonly message?: string }, fallback: string): string {
  if (error.message?.toLowerCase().includes("failed to fetch") || error.message?.toLowerCase().includes("network")) {
    return "The sign-in service is temporarily unreachable. Check the network and try again.";
  }
  switch (error.name) {
    case "NotAuthorizedException": return "Email or password is incorrect.";
    case "UserNotFoundException": return "Email or password is incorrect.";
    case "PasswordResetRequiredException": return "Password reset is required before signing in.";
    case "UserNotConfirmedException": return "This account has not been confirmed. Check your email for the confirmation code.";
    case "CodeMismatchException": return "That reset code is incorrect. Use the latest code from email.";
    case "ExpiredCodeException": return "That reset code has expired. Request a new reset code.";
    case "InvalidPasswordException": return "That password does not meet the account requirements.";
    case "LimitExceededException": return "Too many attempts. Wait a few minutes and try again.";
    default: return error.message ?? fallback;
  }
}

export async function restoreSession(): Promise<AdminSession | null> {
  const email = window.localStorage.getItem(emailKey);
  const refreshToken = window.localStorage.getItem(refreshKey);
  if (email === null || refreshToken === null || pool === null) return null;
  const current = pool.getCurrentUser();
  if (current !== null) {
    const fresh = await currentSession(current).catch(() => null);
    if (fresh !== null) {
      const stored = readSession(email, fresh);
      saveStoredSession(stored);
      return sessionWithRole(stored);
    }
  }
  const refreshed = await refreshStoredSession(email, refreshToken);
  if (refreshed === null) {
    signOut();
    return null;
  }
  saveStoredSession(refreshed);
  return sessionWithRole(refreshed);
}

export async function changePassword(currentPassword: string, nextPassword: string): Promise<void> {
  const current = pool?.getCurrentUser();
  if (current === null || current === undefined) throw new Error("Sign in again before changing your password.");
  await currentSession(current);
  await new Promise<void>((resolve, reject) => {
    current.changePassword(currentPassword, nextPassword, error => {
      if (error) reject(new Error(error.name === "NotAuthorizedException" ? "Current password is incorrect." : "Password change failed."));
      else resolve();
    });
  });
}

export function signOut(): void {
  pool?.getCurrentUser()?.signOut();
  window.localStorage.removeItem(emailKey);
  window.localStorage.removeItem(tokenKey);
  window.localStorage.removeItem(refreshKey);
}

async function sessionWithRole(session: StoredSession): Promise<AdminSession> {
  let response: Response;
  try {
    response = await fetch(`${apiEndpoint.replace(/\/$/u, "")}/v1/admin/me`, {
      headers: { Authorization: `Bearer ${session.idToken}` }
    });
  } catch {
    throw new Error("The Admin service is temporarily unreachable. Check the network and try again.");
  }
  if (response.status === 401 || response.status === 403) {
    signOut();
    throw new Error("This account is not authorized for the Admin Portal.");
  }
  if (!response.ok) throw new Error(`Admin role lookup failed (${String(response.status)}).`);
  const body = await response.json() as { readonly admin?: { readonly email?: string; readonly role?: Role } };
  if (!body.admin?.role) throw new Error("Admin role was not returned.");
  return { email: body.admin.email ?? session.email, role: body.admin.role, idToken: session.idToken };
}

function currentSession(cognitoUser: CognitoUser): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    cognitoUser.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error !== null || session === null || !session.isValid()) reject(error ?? new Error("Session expired."));
      else resolve(session);
    });
  });
}

function refreshStoredSession(email: string, refreshToken: string): Promise<StoredSession | null> {
  return new Promise(resolve => {
    user(email).refreshSession(new CognitoRefreshToken({ RefreshToken: refreshToken }), (error: unknown, session: CognitoUserSession | null) => {
      if (error !== null && error !== undefined || session === null || !session.isValid()) resolve(null);
      else resolve(readSession(email, session));
    });
  });
}

function readSession(email: string, session: CognitoUserSession): StoredSession {
  return {
    email,
    idToken: session.getIdToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken()
  };
}

function saveStoredSession(session: StoredSession): void {
  window.localStorage.setItem(emailKey, session.email);
  window.localStorage.setItem(tokenKey, session.idToken);
  window.localStorage.setItem(refreshKey, session.refreshToken);
}
